/** @jsxImportSource @emotion/react */
import React from 'react';
import MenuBlock from '../../../components/menu-block/menu-block';
import setPhoneStyles from './set-phone.styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Button } from '../../../components/button/button.component';
import { setSetupModal } from '../../../store/setup-modal/setup-modal.slice';
import { ISetUpState } from '../set-up.types';
import toast from 'react-hot-toast';
import {
    selectSetupModal,
    selectSetupModalConnector,
} from '../../../store/setup-modal/setup-modal.selector';
import { AnimationWrapper } from '../components/AnimationWrapper';
import MuiButton from '../../../components/MuiButton/MuiButton';
import { useCreateTransactionMutation } from '../../../api/transactions/transactions.api';
import {
    ContactDetailsForm,
    ContactDetailsFormState,
} from '../../../components/contact-details-form/contact-details-form.component';
import { DEFAULT_CONNECTOR_ID } from '../ConnectorModal';
import { getStationConnectorId, buildPayPath } from '../../../utils/station';

export const PhoneModal = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { stationId } = useParams();
    const { stationLoading } = useOutletContext<{ stationLoading: boolean }>();
    const [isLoading, setIsLoading] = useState(false);
    const [createTransaction] = useCreateTransactionMutation();

    const setUpState: any = useSelector(selectSetupModal);
    const connector = useSelector(selectSetupModalConnector);
    const station = useMemo(() => connector?.station, [connector]);

    const [formState, setFormState] = useState<ContactDetailsFormState>(() => ({
        phone: localStorage.getItem('phone') || '',
        email: localStorage.getItem('email') || '',
        isInvalid: { phone: false, email: false },
    }));
    const [acceptTerms, setAcceptTerms] = useState(false);

    useEffect(() => {
        if (stationLoading) return;
        if (!connector) {
            navigate('/');
        }
    }, [stationLoading, connector, navigate]);

    function handleChangeTerms() {
        setAcceptTerms(!acceptTerms);
    }

    const isStationOnline = station?.online;

    const handleSetSetup = useCallback(
        async (state: ISetUpState) => {
            dispatch(setSetupModal(state));

            if (
                formState.phone &&
                stationId &&
                setUpState?.connector?._id &&
                (setUpState?.energy || setUpState?.duration || setUpState?.disableSessionLimit)
            ) {
                try {
                    setIsLoading(true);
                    const language = localStorage.getItem('language') || 'en';
                    const connectorId = getStationConnectorId(station, setUpState?.connector?._id);
                    const result = await createTransaction({
                        phone: formState.phone,
                        stationId,
                        language: language === 'ua' ? 'uk' : language,
                        ...(setUpState?.energy ? { energy: setUpState?.energy * 1000 } : null),
                        ...(setUpState?.duration ? { duration: setUpState?.duration } : null),
                        ...(formState.email ? { email: formState.email } : null),
                        ...(setUpState?.energy || setUpState?.duration
                            ? null
                            : { disableSessionLimit: true }),
                        connectorId: connectorId ?? DEFAULT_CONNECTOR_ID,
                    });
                    if (result?.data?._id) {
                        navigate(buildPayPath(result.data._id));
                    }
                } catch (error) {
                    setIsLoading(false);
                    console.error('set error', error);
                    toast.error(intl.formatMessage({ id: 'toast.error.checkConnector' }));
                }
            }
        },
        [dispatch, formState.phone, formState.email, setUpState, stationId, navigate, station, createTransaction, intl]
    );

    useEffect(() => {
        if (!setUpState?.energy && !setUpState?.duration && !setUpState?.disableSessionLimit) {
            navigate('/', { replace: true });
        }
    }, [setUpState?.duration, setUpState?.energy, setUpState?.disableSessionLimit]);

    const handleSubmit = useCallback(() => {
        const { phone, email, isInvalid } = formState;
        if (!phone) {
            return;
        }
        if (phone.includes('_')) {
            return;
        }
        localStorage.setItem('phone', phone);
        if (email && !isInvalid.email) {
            localStorage.setItem('email', email);
            handleSetSetup({ phone, email });
            return;
        }
        handleSetSetup({ phone });
    }, [formState, handleSetSetup]);

    const isNextButtonDisabled = useMemo(() => {
        return (
            !isStationOnline ||
            !formState.phone ||
            formState.phone.includes('_') ||
            formState.isInvalid.phone ||
            formState.isInvalid.email ||
            !acceptTerms ||
            (!setUpState?.energy && !setUpState?.duration && !setUpState?.disableSessionLimit)
        );
    }, [
        isStationOnline,
        formState.phone,
        formState.isInvalid.phone,
        formState.isInvalid.email,
        acceptTerms,
    ]);

    return (
        <div css={setPhoneStyles.modalContainer}>
            <MenuBlock station={station} style={{ zIndex: 10002 }} />
            <AnimationWrapper>
                <div css={setPhoneStyles.modalContentBlock}>
                    <ContactDetailsForm
                        defaultPhone={formState.phone}
                        defaultEmail={formState.email}
                        onFormChange={setFormState}
                        showPhoneDescription={true}
                        showEmailDescription={true}
                        requiredPhone={true}
                    />

                    <div css={setPhoneStyles.infoBlockDescription}>
                        <Checkbox checked={acceptTerms} onChange={handleChangeTerms} />
                        <span onClick={handleChangeTerms}>
                            {intl.formatMessage(
                                { id: 'payment.confirmOfferText' },
                                {
                                    0: (
                                        <Link
                                            key="privacy-policy"
                                            css={setPhoneStyles.termsLink}
                                            to={'/privacy-policy'}
                                            target={'_blank'}
                                            rel="noreferrer">
                                            {intl.formatMessage({
                                                id: 'privacyPolicy.title',
                                            })}
                                        </Link>
                                    ),
                                    1: (
                                        <Link
                                            key="terms-of-offer"
                                            css={setPhoneStyles.termsLink}
                                            to={'/terms-of-offer'}
                                            target={'_blank'}
                                            rel="noreferrer">
                                            {intl.formatMessage({
                                                id: 'termsOfOffer.title',
                                            })}
                                        </Link>
                                    ),
                                }
                            )}
                        </span>
                    </div>

                    <div css={setPhoneStyles.actionWrapper}>
                        <MuiButton
                            disabled={isNextButtonDisabled}
                            text={intl.formatMessage({ id: 'next' })}
                            onClick={handleSubmit}
                            isLoading={isLoading}
                        />
                        <Button
                            type={'link'}
                            text={intl.formatMessage({ id: 'back' })}
                            onClick={() => {
                                navigate(-1);
                            }}
                        />
                    </div>
                </div>
            </AnimationWrapper>
        </div>
    );
};
